import React, { useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import type { ClaimCampaignContract, ClaimCsvRow } from '~/services/LuxLoyalty';

import ViewCodesButton from '../ViewCodesButton';

interface Props {
  campaign: ClaimCampaignContract;
  onBack: () => void;
  onProceedToReview: (rows: Array<ClaimCsvRow>, options?: { enrolMember?: boolean }) => void;
}

interface EntryRow {
  id: number;
  customerId: string;
  code: string;
}

export default function SingleClaimStep({ campaign, onBack, onProceedToReview }: Props) {
  const nextIdRef = useRef(1);
  const createEmptyEntry = (): EntryRow => ({ id: nextIdRef.current++, customerId: '', code: '' });
  const [entries, setEntries] = useState<Array<EntryRow>>(() => [createEmptyEntry()]);
  const [enrolMember, setEnrolMember] = useState(false);

  const requiresCode = campaign.validatorType === 'PREGEN_CODE' || campaign.validatorType === 'SHARED_CODE';

  const handleFieldChange = (id: number, field: 'customerId' | 'code', value: string) => {
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)));
  };

  const handleAddEntry = () => {
    setEntries((prev) => [...prev, createEmptyEntry()]);
  };

  const handleRemoveEntry = (id: number) => {
    setEntries((prev) => (prev.length === 1 ? prev : prev.filter((entry) => entry.id !== id)));
  };

  const validEntries = entries.filter((e) => {
    const hasCustomerId = !!e.customerId.trim();
    const hasCode = !!e.code.trim();
    return hasCustomerId && (!requiresCode || hasCode);
  });

  const handleProceedToReview = () => {
    const rows: Array<ClaimCsvRow> = validEntries.map((e) => ({
      customerId: e.customerId.trim(),
      ...(e.code.trim() ? { code: e.code.trim() } : {}),
    }));
    onProceedToReview(rows, { enrolMember });
  };

  const canProceed = validEntries.length > 0;

  const previewRows = validEntries.map((e) => ({
    id: e.id,
    customerId: e.customerId.trim(),
    ...(e.code.trim() ? { code: e.code.trim() } : {}),
  }));

  const previewColumns: Array<GridColDef> = [
    { field: 'customerId', headerName: 'CUSTOMER ID', flex: 1 },
    ...(requiresCode ? [{ field: 'code', headerName: 'CODE', flex: 1 } as GridColDef] : []),
  ];

  return (
    <Box>
      <Grid container spacing={3} columns={12}>
        {/* Left column - Form */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">Claim Entries</Typography>
                <Stack direction="row" spacing={1}>
                  <ViewCodesButton campaign={campaign} />
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddEntry}>
                    Add Entry
                  </Button>
                </Stack>
              </Stack>

              <Stack spacing={2}>
                {entries.map((entry, index) => (
                  <Box key={entry.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Entry {index + 1}
                      </Typography>
                      {entries.length > 1 && (
                        <Tooltip title="Remove entry">
                          <IconButton size="small" onClick={() => handleRemoveEntry(entry.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <Stack spacing={1.5}>
                      <TextField
                        label="Customer ID"
                        value={entry.customerId}
                        onChange={(e) => handleFieldChange(entry.id, 'customerId', e.target.value)}
                        fullWidth
                        required
                        size="small"
                      />
                      {requiresCode && (
                        <TextField
                          label="Code"
                          value={entry.code}
                          onChange={(e) => handleFieldChange(entry.id, 'code', e.target.value)}
                          fullWidth
                          required
                          size="small"
                        />
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box
              sx={{
                bgcolor: enrolMember ? 'rgba(2, 136, 209, 0.08)' : 'action.hover',
                border: 1,
                borderColor: enrolMember ? 'info.main' : 'divider',
                borderRadius: 1,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onClick={() => setEnrolMember((prev) => !prev)}
            >
              <FormControlLabel
                control={<Checkbox checked={enrolMember} onChange={(e) => setEnrolMember(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="subtitle2">Enrol customers into Société</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Customers not yet enrolled will be automatically added to the loyalty program
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, alignItems: 'flex-start' }}
              />
            </Box>
          </Stack>
        </Grid>

        {/* Right column - Preview */}
        <Grid size={{ xs: 12, md: 8 }}>
          {previewRows.length > 0 ? (
            <>
              <Typography variant="subtitle1" mb={1}>
                Preview{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  {previewRows.length} {previewRows.length === 1 ? 'ENTRY' : 'ENTRIES'}
                </Typography>
              </Typography>
              <DataGrid
                rows={previewRows}
                columns={previewColumns}
                getRowId={(row) => row.id}
                density="compact"
                hideFooter={previewRows.length <= 100}
                autoHeight
                disableColumnMenu
                disableRowSelectionOnClick
              />
            </>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Typography color="text.secondary">Enter a customer ID to continue</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Bottom bar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
        pt={2}
        borderTop={1}
        borderColor="divider"
      >
        <Box>
          {validEntries.length > 0 && (
            <Typography variant="body2" color="success.main">
              {validEntries.length} {validEntries.length === 1 ? 'entry' : 'entries'} will be processed
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="text" onClick={onBack}>
            Back
          </Button>
          <Button variant="contained" onClick={handleProceedToReview} disabled={!canProceed}>
            Proceed to Review
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
